import type {
  IncidentCategory,
  IncidentPriority,
  IncidentStatus,
  IncidentSubCategory,
} from '../../types/extranet-api.types';

export const CATEGORY_LABELS: Record<IncidentCategory, string> = {
  pickup_delay: 'Retard de prise en charge',
  driver_absent: 'Absence chauffeur',
  security: 'Incident sécurité',
  vehicle: 'Véhicule',
  agent_pickup: 'Prise en charge agent',
  communication: 'Communication',
};

export const CATEGORY_SHORT_LABELS: Record<IncidentCategory, string> = {
  pickup_delay: 'Retard',
  driver_absent: 'Absence chauffeur',
  security: 'Sécurité',
  vehicle: 'Véhicule',
  agent_pickup: 'PEC agent',
  communication: 'Communication',
};

export const SUB_CATEGORY_LABELS: Record<IncidentSubCategory, string> = {
  delay_under_10: 'Retard < 10 min',
  delay_10_30: 'Retard 10-30 min',
  delay_over_30: 'Retard > 30 min',
  no_show_driver: 'Non présentation',
  driver_cancel: 'Annulation chauffeur',
  speeding: 'Excès de vitesse',
  dangerous_driving: 'Comportement de conduite dangereux',
  line_crossing: 'Franchissement de ligne blanche',
  phone_use: 'Utilisation du téléphone',
  no_seatbelt: 'Non-port de la ceinture',
  cleanliness: 'Propreté insuffisante',
  non_compliance: 'Non-conformité du véhicule',
  breakdown: 'Panne',
  wrong_location: 'Erreur de lieu',
  agent_not_found: 'Agent non trouvé',
  identification_error: "Erreur d'identification",
  wrong_info: 'Information erronée',
  notification_failure: 'Défaut de notification',
  app_issue: 'Problème applicatif',
};

export const SUB_CATEGORIES_BY_CATEGORY: Record<IncidentCategory, IncidentSubCategory[]> = {
  pickup_delay: ['delay_under_10', 'delay_10_30', 'delay_over_30'],
  driver_absent: ['no_show_driver', 'driver_cancel'],
  security: ['speeding', 'dangerous_driving', 'line_crossing', 'phone_use', 'no_seatbelt'],
  vehicle: ['cleanliness', 'non_compliance', 'breakdown'],
  agent_pickup: ['wrong_location', 'agent_not_found', 'identification_error'],
  communication: ['wrong_info', 'notification_failure', 'app_issue'],
};

export const STATUS_LABELS: Record<IncidentStatus, string> = {
  open: 'Ouvert',
  in_progress: 'En cours',
  waiting_client: 'En attente client',
  resolved: 'Résolu',
  closed: 'Clôturé',
};

interface StatusColorTokens {
  bg: string;
  text: string;
  dot: string;
}

export const STATUS_COLORS: Record<IncidentStatus, StatusColorTokens> = {
  open: { bg: 'bg-zinc-100', text: 'text-zinc-700', dot: 'bg-zinc-400' },
  in_progress: { bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-500' },
  waiting_client: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  resolved: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  closed: { bg: 'bg-zinc-50', text: 'text-zinc-500', dot: 'bg-zinc-400' },
};

export const PRIORITY_LABELS: Record<IncidentPriority, string> = {
  medium: 'Moyenne',
  high: 'Haute',
};

export const PRIORITY_COLORS: Record<IncidentPriority, { dot: string; text: string }> = {
  high: { dot: 'bg-red-500', text: 'text-zinc-700' },
  medium: { dot: 'bg-amber-500', text: 'text-zinc-700' },
};

export const PRIORITY_ORDER: Record<IncidentPriority, number> = { high: 0, medium: 1 };

export const STATUS_ORDER: Record<IncidentStatus, number> = {
  open: 0,
  in_progress: 1,
  waiting_client: 2,
  resolved: 3,
  closed: 4,
};

export const ALL_STATUSES: IncidentStatus[] = ['open', 'in_progress', 'waiting_client', 'resolved', 'closed'];

/**
 * Tant que le back livre la taxonomie 4 valeurs simplifiée (cf. CATEGORY_BACK_TO_FRONT
 * dans incident.ts), seules `security` et `communication` sont effectivement émises.
 * Les 4 autres catégories front (pickup_delay/driver_absent/vehicle/agent_pickup)
 * restent disponibles dans les Records de labels pour le jour où le back livrera
 * la taxonomie complète — mais on ne les expose ni dans le filtre catégorie ni
 * dans les buckets dashboard pour ne pas afficher 4 cases mortes (0 résultat).
 */
export const ALL_CATEGORIES: IncidentCategory[] = ['security', 'communication'];

export const ALL_PRIORITIES: IncidentPriority[] = ['high', 'medium'];
